import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AdminProvider, GroupProvider, MemberProvider, useAdmin, useMember } from './context/AuthContext';
import AdminLayout from './components/AdminLayout';
import MemberLayout from './components/MemberLayout';
import { PageLoader } from './components/ui';

import Home from './pages/member/Home';
import Register from './pages/member/Register';
import Login from './pages/member/Login';
import ForgotPassword from './pages/member/ForgotPassword';
import ResetPassword from './pages/member/ResetPassword';
import VerifyEmail from './pages/member/VerifyEmail';
import ConfirmDetails from './pages/member/ConfirmDetails';
import Unsubscribe from './pages/member/Unsubscribe';
import Privacy from './pages/member/Privacy';
import Profile from './pages/member/Profile';
import Documents from './pages/member/Documents';

import AdminLogin from './pages/admin/AdminLogin';
import Dashboard from './pages/admin/Dashboard';
import Members from './pages/admin/Members';
import Layouts from './pages/admin/Layouts';
import Notifications from './pages/admin/Notifications';
import NotificationCompose from './pages/admin/NotificationCompose';
import NotificationReport from './pages/admin/NotificationReport';
import AdminDocuments from './pages/admin/AdminDocuments';
import Admins from './pages/admin/Admins';
import Settings from './pages/admin/Settings';

const MemberOnly = ({ children }) => {
  const { member, loading } = useMember();
  if (loading) return <MemberLayout><PageLoader /></MemberLayout>;
  if (!member) return <Navigate to="/login" replace />;
  return <MemberLayout>{children}</MemberLayout>;
};

const AdminOnly = ({ children }) => {
  const { admin, loading } = useAdmin();
  if (loading) return <PageLoader />;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return <AdminLayout>{children}</AdminLayout>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/confirm-details" element={<ConfirmDetails />} />
      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/profile" element={<MemberOnly><Profile /></MemberOnly>} />
      <Route path="/documents" element={<MemberOnly><Documents /></MemberOnly>} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminOnly><Dashboard /></AdminOnly>} />
      <Route path="/admin/members" element={<AdminOnly><Members /></AdminOnly>} />
      <Route path="/admin/layouts" element={<AdminOnly><Layouts /></AdminOnly>} />
      <Route path="/admin/notifications" element={<AdminOnly><Notifications /></AdminOnly>} />
      <Route path="/admin/notifications/new" element={<AdminOnly><NotificationCompose /></AdminOnly>} />
      <Route path="/admin/notifications/:id/edit" element={<AdminOnly><NotificationCompose /></AdminOnly>} />
      <Route path="/admin/notifications/:id" element={<AdminOnly><NotificationReport /></AdminOnly>} />
      <Route path="/admin/documents" element={<AdminOnly><AdminDocuments /></AdminOnly>} />
      <Route path="/admin/admins" element={<AdminOnly><Admins /></AdminOnly>} />
      <Route path="/admin/settings" element={<AdminOnly><Settings /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <GroupProvider>
      <MemberProvider>
        <AdminProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
          </BrowserRouter>
        </AdminProvider>
      </MemberProvider>
    </GroupProvider>
  );
}
